<script>
import { mapState, mapActions } from 'vuex';
import store from '../../../store';
import Vue from 'vue';
import TwCopyAndTranslateModal from '../../../components/TwCopyAndTranslateModal.vue';

export default {
  components: {
    TwCopyAndTranslateModal,
  },
  data() {
    return {
      headers: [
        { text: '', sortable: false, width: '10%' },
        { text: 'Title', sortable: false, width: '35%' },
        { text: 'Description', sortable: false, width: '45%' },
        { text: '', sortable: false, width: '10%' },
      ],
      textRule: [ v => !!v || 'Text cannot be empty' ],
      clonedPickupPlaces: [],
      pickupPlacesValid: false,
      copyAndTranslateModalOpen: false,
    };
  },
  computed: {
    ...mapState({
      pickupPlaces: state => state.areas.pickupPlaces.items,
      loading: state => state.areas.pickupPlaces.loading,
      error: state => state.areas.pickupPlaces.error,
      area: state => state.areas.area,
      xmlProducts: state => state.twenty.xmlProducts[6].items,
      xmlChannels: state => state.twenty.xmlChannels.items,
      selectedDomainId: state => state.ski.selectedDomainId,
      domains: state => state.twenty.domains.items,
    }),
    xmlProductsWithChannels() {
      return this.xmlProducts.map(
        product => ({ ...product, xmlChannel: this.xmlChannels.filter(c => c.id === product.xmlChannelId)[0] }),
      );
    },
    getCopyValues() {
      const values = [];

      this.clonedPickupPlaces.forEach(place => {
        values.push(
          {
            globalModelId: place.id,
            globalModelColumnName: 'area_pickup_id',
            selectKeyOverrides: [ 'domain_id', 'area_pickup_id' ],
            tableName: 'area_pickup_domain',
            sourceDomainId: null,
            selectedFromDomain: null,
            fromLanguageIsoOverride: null,
            sourceIsoOverride: null,
            disableSourceIsoOverride: true,
            toDomains: [],
            shouldTranslate: false,
            shouldCopySeo: false,
            fieldNamesAndValuesToCopyAndTranslate: [
              { title: place.title },
              { description: place.description },
              { domain_id: null },
              { area_pickup_id: place.id },
            ],
            title: `Pickup: ${place.title}`,
          },
        );
      });
      return values;
    },
  },
  methods: {
    ...mapActions({
      fetchPickupPlaces: 'areas/fetchPickupPlaces',
      updatePickupPlaces: 'areas/updatePickupPlaces',
      loadXmlChannels: 'twenty/xmlChannels',
      loadXmlProducts: 'twenty/xmlProducts',
    }),
    createNewPickupPlace() {
      this.clonedPickupPlaces.unshift({
        id: null,
        areaId: this.area.id,
        title: '',
        description: '',
        xmlChannelProductId: null,
        domains: {},
      });
    },
    save() {
      if (this.$refs.pickupPlacesForm.validate()) {
        this.updatePickupPlaces(this.clonedPickupPlaces);
      }
    },
    handleDomains() {
      if (this.selectedDomainId !== null) {
        this.clonedPickupPlaces.forEach(pickupPlace => {
          if (Array.isArray(pickupPlace.domains)) {
            Vue.set(pickupPlace, 'domains', {});
          }
          if (!pickupPlace.domains[this.selectedDomainId]) {
            pickupPlace.domains[this.selectedDomainId] = {
              domainId: this.selectedDomainId,
              title: '',
              description: '',
            };
          }
        });
      }
    },
  },
  mounted() {
    this.clonedPickupPlaces = [ ...this.pickupPlaces ];
    this.loadXmlChannels(3);
    this.loadXmlProducts(6);
    this.handleDomains();
  },
  watch: {
    pickupPlaces() {
      this.clonedPickupPlaces = [ ...this.pickupPlaces ];
      this.handleDomains();
    },
    selectedDomainId() {
      this.handleDomains();
    },
  },
  beforeRouteEnter(to, from, next) {
    store.dispatch('areas/fetchPickupPlaces', to.params.id)
      .then(() => {
        next();
      })
      .catch(() => {
        next();
      });
  },
};
</script>

<template>
  <v-flex>
    <v-flex>
      <v-btn
        :disabled="!pickupPlacesValid"
        color="green"
        flat
        @click="save"
      >
        Save
      </v-btn>
      <v-btn
        color="blue darken-1"
        flat
        @click="createNewPickupPlace"
      >
        Add new pickup place
      </v-btn>
      <v-btn
        v-if="clonedPickupPlaces.length"
        :loading="loading"
        color="primary success"
        @click="copyAndTranslateModalOpen = !copyAndTranslateModalOpen"
      >
        Copy/translate
      </v-btn>
      <tw-copy-and-translate-modal
        v-if="copyAndTranslateModalOpen"
        :elements-to-handle="getCopyValues"
        :domains="domains"
      />
    </v-flex>
    <v-form
      ref="pickupPlacesForm"
      v-model="pickupPlacesValid"
      lazy-validation
      class="px-4"
    >
      <div
        v-for="(pickupPlace, index) in clonedPickupPlaces"
        :key="`pickup-place-${index}`"
        class="d-flex flex-col flex-wrap"
      >
        <div class="d-flex flex-row align-start">
          <v-flex xs2 class="px-2">
            {{ pickupPlace.id ? 'EDIT' : 'NEW' }}
          </v-flex>

          <v-flex xs4 class="px-2">
            <v-text-field
              v-model="pickupPlace.title"
              :rules="textRule"
              label="Global Title"
            />
            <v-text-field
              v-if="selectedDomainId !== null"
              v-model="pickupPlace.domains[selectedDomainId].title"
              :rules="textRule"
              label="Domain Title"
            />
          </v-flex>

          <v-flex xs4 class="px-2">
            <v-textarea
              v-model="pickupPlace.description"
              :rules="textRule"
              label="Global Description"
              class="px-2"
            />
            <v-textarea
              v-if="selectedDomainId !== null"
              v-model="pickupPlace.domains[selectedDomainId].description"
              :rules="textRule"
              label="Domain Description"
              class="px-2"
            />
          </v-flex>
          <v-btn
            v-if="!pickupPlace.id"
            color="red darken-1"
            flat
            @click="clonedPickupPlaces.splice(index, 1)"
          >
            Delete
          </v-btn>
        </div>

        <div class="d-flex">
          <v-flex xs2 class="px-2"/>
          <v-flex xs10 class="px-2">
            <v-autocomplete
              v-model="pickupPlace.xmlChannelProductId"
              :items="xmlProductsWithChannels"
              label="XML Pick Up"
              item-value="id"
              item-text="tbName"
              clearable
            >
              <template v-slot:selection="data">
                {{ data.item.xmlChannel.name }} - {{ data.item.xmlChannel.webservice }} - {{ data.item.tbName }}
              </template>
              <template v-slot:item="data">
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ data.item.xmlChannel.name }} - {{ data.item.xmlChannel.webservice }} - {{ data.item.tbName }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </template>
            </v-autocomplete>
          </v-flex>
        </div>

        <v-divider v-if="index !== clonedPickupPlaces.length - 1" class="py-4"/>
      </div>
    </v-form>
  </v-flex>
</template>



// WEBPACK FOOTER //
// src/pages/ski/areas/SkiAreaPickupPlaces.vue